import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import ContractEvaluatorLauncher from "@/components/ContractEvaluatorLauncher";
import { useAuth } from "@/context/AuthContext";
import { currency, STAGE_LABELS } from "@/lib/constants";
import SLABadge from "@/components/SLABadge";
import ReferralsSummaryTile from "@/components/ReferralsSummaryTile";
import { AlertTriangle, Clock, FileText, Users, Calendar, Bell, Shield, RefreshCw, Flag, Activity, X, Send, Target, Sparkles, FileEdit, Eye, UserCheck } from "lucide-react";

function DigestPreviewModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!open) return;
    setBusy(true);
    api.get("/reports/compliance-digest/preview")
      .then(r => setData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Preview failed"))
      .finally(() => setBusy(false));
  }, [open]);
  const testSend = async () => {
    setSending(true);
    try {
      const r = await api.post("/reports/compliance-digest/test-send");
      toast.success(r.data.sent ? `Test digest emailed to ${r.data.to}` : "Send accepted but not confirmed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="digest-preview-modal">
      <div className="bg-white rounded-md max-w-3xl w-full max-h-[85vh] flex flex-col shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900">Weekly Compliance Digest — Preview</div>
            <div className="text-[11px] text-slate-500">
              {data ? `Window: last 7 days from ${new Date(data.window_start).toLocaleDateString()} · Recipients: ${data.recipients.length}` : "Loading…"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={testSend} disabled={sending || busy}
                    className="h-8 px-3 text-xs font-semibold rounded border border-[#005289] text-[#005289] hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1.5"
                    data-testid="digest-test-send-btn">
              <Send className="w-3 h-3" /> {sending ? "Sending…" : "Send test to me"}
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="digest-preview-close"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {busy && <div className="text-sm text-slate-500 text-center py-8">Loading preview…</div>}
          {data && (
            <>
              {data.recipients.length > 0 && (
                <div className="text-[11px] text-slate-500">
                  Will be emailed to: {data.recipients.map(r => r.name || r.email).join(", ")}
                </div>
              )}
              {data.contracts.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-6 border border-dashed border-slate-200 rounded" data-testid="digest-preview-empty">
                  No PHI/BAA contracts opened in the last 7 days — Monday's digest will not send.
                </div>
              ) : (
                <table className="hcd-table">
                  <thead><tr><th>#</th><th>Vendor</th><th>Dept</th><th>Stage</th><th className="num">Annual</th><th>PHI / BAA</th></tr></thead>
                  <tbody>
                    {data.contracts.map(c => (
                      <tr key={c.id} data-testid={`digest-row-${c.contract_number}`}>
                        <td className="font-mono text-xs">{c.contract_number}</td>
                        <td className="font-semibold">{c.vendor_legal_name}</td>
                        <td className="text-xs">{c.department}</td>
                        <td className="text-xs">{c.stage}</td>
                        <td className="num">{c.contract_amount ? `$${c.contract_amount.toLocaleString()}` : "—"}</td>
                        <td className="text-xs">
                          {c.handles_phi_pii_pci && <span className="chip chip-duesoon"><span className="dot" />PHI</span>}
                          {" "}
                          {c.has_phi_flag && <span className="chip chip-overdue"><span className="dot" />BAA Flag</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ComplianceSLAChip({ summary }) {
  if (!summary || summary.compliance_sla_avg_days == null) return null;
  const days = summary.compliance_sla_avg_days;
  const color = days <= 3 ? "#00AE42" : days <= 7 ? "#F59E0B" : "#B91C1C";
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3"
         data-testid="compliance-sla-widget"
         title={`Average time from Executed → Compliance Cleared (${summary.compliance_sla_sample} sample)`}>
      <div className="text-2xl font-bold" style={{ color }}>{days}<span className="text-sm font-normal">d</span></div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Cleared cycle time</div>
        <div className="text-xs text-slate-800">Executed → Cleared avg</div>
        <div className="text-[10px] text-slate-400">n = {summary.compliance_sla_sample}</div>
      </div>
    </div>
  );
}

function ClearedTrendDrill({ open, onClose, month }) {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!open || !month) return;
    setRows(null);
    api.get(`/reports/cleared-in-month?month=${month}`)
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]));
  }, [open, month]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="trend-drill-modal">
      <div className="bg-white rounded-md max-w-3xl w-full max-h-[85vh] flex flex-col shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900">Contracts Cleared in {month}</div>
            <div className="text-[11px] text-slate-500">{rows ? `${rows.length} contract${rows.length===1?"":"s"}` : "Loading…"}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="trend-drill-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {rows === null && <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>}
          {rows && rows.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No contracts were cleared in this month.</div>}
          {rows && rows.length > 0 && (
            <table className="hcd-table">
              <thead><tr><th>#</th><th>Vendor</th><th>Dept</th><th className="num">Annual</th><th>Cleared by</th><th>Cleared at</th></tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} onClick={() => nav(`/contract/${r.id}`)} className="cursor-pointer hover:bg-slate-50" data-testid={`trend-row-${r.contract_number}`}>
                    <td className="font-mono text-xs">{r.contract_number}</td>
                    <td className="font-semibold">{r.vendor_legal_name}</td>
                    <td className="text-xs">{r.department}</td>
                    <td className="num">{r.contract_amount ? `$${r.contract_amount.toLocaleString()}` : "—"}</td>
                    <td className="text-xs">{r.cleared_by_name}</td>
                    <td className="text-xs text-slate-500">{r.cleared_at ? new Date(r.cleared_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function ClearedVsPendingWidget({ summary, onClick, onMonthClick }) {
  if (!summary) return null;
  const pct = summary.executed_cleared_pct ?? 0;
  const cleared = summary.executed_cleared ?? 0;
  const pending = summary.executed_uncleared ?? 0;
  const total = summary.executed ?? (cleared + pending);
  if (total === 0) return null;
  const color = pct >= 80 ? "#00AE42" : pct >= 50 ? "#F59E0B" : "#B91C1C";
  const trend = (summary.cleared_trend || []).slice(-12);
  const trendMax = Math.max(100, ...trend.map(t => t.pct));
  const pathD = trend.length > 1
    ? trend.map((t, i) => `${i === 0 ? "M" : "L"} ${(i / (trend.length - 1)) * 100},${30 - (t.pct / trendMax) * 28}`).join(" ")
    : "";
  return (
    <div onClick={onClick}
         className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3 cursor-pointer hover:border-[#005289] hover:shadow-sm transition"
         data-testid="cleared-vs-pending-widget"
         title="Percent of executed contracts stamped Compliance Cleared">
      <div className="relative w-11 h-11 shrink-0">
        <svg viewBox="0 0 36 36" className="w-11 h-11 -rotate-90">
          <circle cx="18" cy="18" r="15.9155" fill="transparent" stroke="#E2E8F0" strokeWidth="3.2" />
          <circle cx="18" cy="18" r="15.9155" fill="transparent" stroke={color} strokeWidth="3.2"
                  strokeDasharray={`${pct} ${100 - pct}`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold" style={{ color }}>{pct}%</div>
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Compliance Cleared</div>
        <div className="text-xs text-slate-800">
          <b>{cleared}</b> cleared · <b>{pending}</b> pending
          <span className="text-slate-400"> of {total} executed</span>
        </div>
        {trend.length > 1 && (
          <svg viewBox="0 0 100 30" width="120" height="20" className="mt-0.5 cursor-pointer" data-testid="cleared-trend-sparkline"
               onClick={(e) => { e.stopPropagation(); }}>
            <path d={pathD} stroke={color} strokeWidth="1.5" fill="none" />
            {trend.map((t, i) => (
              <circle key={t.month} cx={(i / (trend.length - 1)) * 100} cy={30 - (t.pct / trendMax) * 28}
                      r="2.5" fill={color} opacity="0.85"
                      onClick={(e) => { e.stopPropagation(); onMonthClick && onMonthClick(t.month); }}
                      data-testid={`trend-dot-${t.month}`}>
                <title>{`${t.month} · ${t.pct}% (${t.cleared}/${t.executed})`}</title>
              </circle>
            ))}
          </svg>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub, icon: Icon, color = "#005289", onClick, testid }) {
  const clickable = !!onClick;
  return (
    <div onClick={onClick}
         className={`bg-white border border-slate-200 rounded-md p-4 ${clickable ? "cursor-pointer hover:border-[#005289] hover:shadow-sm transition" : ""}`}
         data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: color + "15" }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      {clickable && <div className="mt-2 text-[10px] text-[#005289] font-semibold">Click to drill down →</div>}
    </div>
  );
}

function DrillPanel({ open, onClose, title, rows, columns, onRow }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="dash-drill-modal">
      <div className="bg-white rounded-md max-w-5xl w-full max-h-[85vh] flex flex-col shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900">{title}</div>
            <div className="text-[11px] text-slate-500">{rows.length} record{rows.length===1?"":"s"}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="dash-drill-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {rows.length === 0 && <div className="text-center py-6 text-slate-500 text-sm">Nothing to show.</div>}
          {rows.length > 0 && (
            <table className="hcd-table">
              <thead><tr>{columns.map(c => <th key={c.key} className={c.num ? "num" : ""}>{c.label}</th>)}</tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || r.contract_id || i} onClick={() => onRow && onRow(r)} className="cursor-pointer hover:bg-slate-50">
                    {columns.map(c => <td key={c.key} className={c.num ? "num" : ""}>{c.render ? c.render(r) : r[c.key]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function CoachModal({ user, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!user) return;
    api.get(`/reports/kpi-coach/${user.user_id}`).then(r => setData(r.data)).catch(() => {});
  }, [user]);
  if (!user) return null;
  const sevColor = { positive: "#00AE42", medium: "#F59E0B", high: "#EF4444" };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="coach-modal">
      <div className="bg-white rounded-md max-w-2xl w-full max-h-[85vh] flex flex-col shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900 flex items-center gap-2"><Target className="w-4 h-4 text-[#005289]" />Team Coach — {user.name}</div>
            <div className="text-[11px] text-slate-500">Private tips based on this member's KPI trend</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="coach-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-3">
          {!data && <div className="text-sm text-slate-500 text-center py-8">Loading tips...</div>}
          {data && (
            <>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="p-2 bg-slate-50 rounded border border-slate-200"><div className="text-slate-500">Open</div><div className="font-bold text-lg">{data.metrics.assigned_open}</div></div>
                <div className="p-2 bg-slate-50 rounded border border-slate-200"><div className="text-slate-500">Executed 30d</div><div className="font-bold text-lg">{data.metrics.executed_30d}</div></div>
                <div className="p-2 bg-slate-50 rounded border border-slate-200"><div className="text-slate-500">Avg Cycle / SLA</div><div className="font-bold">{data.metrics.avg_cycle_days ?? "—"}d / {data.metrics.sla_compliance_pct == null ? "—" : `${data.metrics.sla_compliance_pct}%`}</div></div>
              </div>
              {data.tips.map((t, i) => (
                <div key={i} className="border rounded-md p-3" style={{ borderColor: sevColor[t.severity] || "#E2E8F0", background: `${sevColor[t.severity] || "#005289"}0d` }} data-testid={`coach-tip-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="chip" style={{ background: sevColor[t.severity], color: "white" }}><span className="dot" />{t.severity}</span>
                    <div className="text-sm font-bold text-slate-900">{t.headline}</div>
                  </div>
                  <div className="text-xs text-slate-700 mt-1.5">{t.body}</div>
                </div>
              ))}
              <div className="text-[10px] text-slate-400 pt-2">Generated {new Date(data.generated_at).toLocaleString()}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function UniversityBadge() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/university/progress").then(r => setP(r.data)).catch(() => {}); }, []);
  if (!p) return null;
  const pct = p.total ? Math.round((p.watched / p.total) * 100) : 0;
  return (
    <a href="/university" className="block bg-white border border-slate-200 rounded-md p-3 hover:border-[#005289] hover:shadow-sm transition" data-testid="uni-badge">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#005289] to-[#0a7bc7] flex items-center justify-center text-white">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3z"/></svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-slate-900">Contract University</div>
          <div className="text-[11px] text-slate-500">{p.watched}/{p.total} lessons · Track A {p.track_a.watched}/{p.track_a.total} · Track B {p.track_b.watched}/{p.track_b.total}</div>
          <div className="h-1.5 bg-slate-100 rounded mt-1.5 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} data-testid="uni-badge-bar" />
          </div>
        </div>
        <div className="text-lg font-bold text-[#005289]">{pct}%</div>
      </div>
    </a>
  );
}

function ownerName(c, team) {
  const ownerId = c.assigned_attorney_id || c.assigned_analyst_id || c.assigned_gc_id;
  if (!ownerId) return "—";
  const u = team.find(t => t.id === ownerId);
  return u ? u.name.split(" ")[0] + " " + (u.name.split(" ")[1] || "").slice(0,1) + "." : "—";
}

function GCKPIPanel({ kpis, onDrill, onCoach }) {
  if (!kpis) return null;
  const { org, per_user, config } = kpis;
  const targets = config?.targets || {};
  const kpiCard = (label, val, target, ok, unit = "", key, testid) => (
    <div onClick={() => onDrill(key)}
         className="bg-white border border-slate-200 rounded-md p-4 cursor-pointer hover:border-[#005289] hover:shadow-sm transition"
         data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-2xl font-bold ${ok === false ? "text-red-600" : "text-slate-900"}`}>
        {val == null ? "—" : val}{unit}
      </div>
      {target != null && <div className="text-[10px] text-slate-500 mt-0.5">Target: {target}{unit} · {ok === false ? "Below target" : "On track"}</div>}
      <div className="mt-1 text-[10px] text-[#005289] font-semibold">Drill down →</div>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="gc-kpi-panel">
      <div className="flex items-center gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-[#005289]">General Counsel KPI Command</div>
        <span className="text-[10px] text-slate-500">Configurable in IT Admin › System Settings</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {(config?.enabled || []).includes("cycle_time") && kpiCard("Avg Cycle Days", org.avg_cycle_days, targets.cycle_time_days,
          org.avg_cycle_days == null || org.avg_cycle_days <= (targets.cycle_time_days || 21), "d", "cycle", "kpi-card-cycle")}
        {(config?.enabled || []).includes("sla_compliance") && kpiCard("SLA Compliance", org.sla_compliance_pct, targets.sla_compliance_pct,
          org.sla_compliance_pct == null || org.sla_compliance_pct >= (targets.sla_compliance_pct || 90), "%", "sla", "kpi-card-sla")}
        {(config?.enabled || []).includes("throughput") && kpiCard("Executed (30d)", org.total_executed_30d, null, true, "", "throughput", "kpi-card-throughput")}
        {(config?.enabled || []).includes("workload") && kpiCard("Open Assignments", org.total_open, null, true, "", "workload", "kpi-card-workload")}
        {(config?.enabled || []).includes("open_red_flags") && kpiCard("Open Red Flags", org.total_open_red_flags, null, org.total_open_red_flags === 0, "", "flags", "kpi-card-flags")}
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <div className="px-5 py-3 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">Analyst / Attorney Performance</div>
        <table className="hcd-table" data-testid="gc-kpi-table">
          <thead><tr><th>Name</th><th>Role</th><th className="num">Open</th><th className="num">Executed</th><th className="num">Executed 30d</th><th className="num">Avg Cycle (d)</th><th className="num">SLA %</th><th className="num">Avg First Resp (h)</th><th className="num">Open Flags</th><th className="num">Score</th><th className="num"></th></tr></thead>
          <tbody>
            {per_user.map(r => (
              <tr key={r.user_id} data-testid={`kpi-user-${r.user_id}`}>
                <td className="font-semibold">{r.name}</td>
                <td className="text-xs text-slate-500">{r.role?.replace(/_/g, " ")}</td>
                <td className="num">{r.assigned_open}</td>
                <td className="num">{r.executed}</td>
                <td className="num font-bold">{r.executed_30d}</td>
                <td className={`num ${r.meets_cycle_target ? "" : "text-red-600 font-bold"}`}>{r.avg_cycle_days ?? "—"}</td>
                <td className={`num ${r.meets_sla_target ? "" : "text-red-600 font-bold"}`}>{r.sla_compliance_pct == null ? "—" : `${r.sla_compliance_pct}%`}</td>
                <td className={`num ${r.meets_first_response_target ? "" : "text-red-600 font-bold"}`}>{r.avg_first_response_hours ?? "—"}</td>
                <td className={`num ${r.open_red_flags === 0 ? "" : "text-amber-600 font-bold"}`}>{r.open_red_flags}</td>
                <td className="num">
                  <span className={`chip ${r.score === 3 ? "chip-ontrack" : r.score === 2 ? "chip-duesoon" : "chip-overdue"}`}>
                    <span className="dot" />{r.score}/3
                  </span>
                </td>
                <td className="num">
                  <button onClick={() => onCoach(r)} className="text-xs font-bold text-[#005289] hover:underline flex items-center gap-1" data-testid={`coach-btn-${r.user_id}`}>
                    <Sparkles className="w-3 h-3" /> Coach
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ContractsTable({ contracts, onOpen, team = [], empty = "No contracts", showOwner = true, showSubmitter = false }) {
  if (!contracts.length) return <div className="text-center py-6 text-slate-500 text-sm">{empty}</div>;
  return (
    <div className="overflow-x-auto">
      <table className="hcd-table">
        <thead>
          <tr>
            <th>#</th><th>Vendor</th><th>Dept</th><th>Stage</th>
            {showOwner && <th>Owner</th>}
            {showSubmitter && <th>Submitted by</th>}
            <th>Executed</th>
            <th>SLA</th><th className="num">Days</th><th className="num">$</th>
          </tr>
        </thead>
        <tbody>
          {contracts.map(c => (
            <tr key={c.id} onClick={() => onOpen(c.id)} className="cursor-pointer">
              <td className="font-mono text-xs">{c.contract_number}</td>
              <td className="font-semibold">{c.vendor_legal_name}</td>
              <td className="text-slate-600">{c.department}</td>
              <td>{STAGE_LABELS[c.stage]}</td>
              {showOwner && <td className="text-xs">{ownerName(c, team)}</td>}
              {showSubmitter && <td className="text-xs text-slate-600">{c.submitter_email || c.created_by || "—"}</td>}
              <td className="text-xs text-slate-500">{c.executed_date ? new Date(c.executed_date).toLocaleDateString() : "—"}</td>
              <td><SLABadge status={c.sla_status} remaining={c.sla_remaining} /></td>
              <td className="num">{c.days_total}</td>
              <td className="num">{currency(c.contract_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MyWorkDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [all, setAll] = useState([]);
  const [team, setTeam] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [priorityList, setPriorityList] = useState([]);
  const [priorityFeed, setPriorityFeed] = useState([]);
  const [complianceCounts, setComplianceCounts] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [drill, setDrill] = useState({ open: false, title: "", rows: [], columns: [] });
  const [coachUser, setCoachUser] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [approverPending, setApproverPending] = useState({ count: 0, items: [] });
  const [upcomingMs, setUpcomingMs] = useState([]);
  const [digestPreview, setDigestPreview] = useState(false);
  const [trendMonth, setTrendMonth] = useState(null);

  useEffect(() => {
    if (!user) return;
    let url = "/contracts";
    if (["attorney", "contract_analyst", "general_counsel"].includes(user.role)) url = `/contracts?assigned_to=${user.id}`;
    api.get(url).then(r => setAll(r.data)).catch(() => {});
    api.get("/users/team").then(r => setTeam(r.data || [])).catch(() => {});
    api.get("/notifications").then(r => setNotifs(r.data || [])).catch(() => {});
    api.get("/reports/summary").then(r => { setRenewals(r.data.renewals || []); setSummary(r.data); }).catch(() => {});
    api.get("/reports/priority").then(r => setPriorityList(r.data || [])).catch(() => {});
    api.get("/reports/priority-updates?limit=8").then(r => setPriorityFeed(r.data || [])).catch(() => {});
    api.get("/contracts/mine/drafts").then(r => setDrafts(r.data || [])).catch(() => {});
    api.get("/approver/pending").then(r => setApproverPending(r.data || { count: 0, items: [] })).catch(() => {});
    api.get("/milestones/upcoming?days=45").then(r => setUpcomingMs(r.data || [])).catch(() => {});
    if (["compliance", "general_counsel", "it_admin"].includes(user.role)) {
      api.get("/compliance/dashboard").then(r => setComplianceCounts(r.data.counts)).catch(() => {});
    }
    if (user.role === "general_counsel") {
      api.get("/reports/kpis").then(r => setKpis(r.data)).catch(() => {});
    }
  }, [user]);

  if (!user) return null;

  const assigned = all.filter(c => c.stage !== "executed");
  const active = assigned.filter(c => c.stage !== "unassigned");
  const overdue = assigned.filter(c => c.sla_status === "overdue");
  const dueSoon = assigned.filter(c => c.sla_status === "due_soon");
  const executedRecent = all.filter(c => c.stage === "executed")
    .sort((a, b) => (b.executed_date || "").localeCompare(a.executed_date || ""))
    .slice(0, 8);
  const unassignedList = all.filter(c => c.stage === "unassigned");
  const unread = notifs.filter(n => !n.read);
  const mentions = unread.filter(n => (n.title || "").toLowerCase().includes("mention"));

  const roleFocus = {
    general_counsel: { title: "General Counsel Command", subtitle: "Awaiting your GC review + final approvals" },
    legal_manager: { title: "Director of Legal Team", subtitle: "Triage queue, team workload & SLA health" },
    attorney: { title: "My Attorney Queue", subtitle: "Contracts assigned to you for legal review" },
    contract_analyst: { title: "My Analyst Queue", subtitle: "Contracts you're preparing for attorney review" },
    compliance: { title: "Compliance Watch", subtitle: "Flags, PHI/BAA gaps & checklist exceptions" },
    records_management: { title: "Records Room", subtitle: "Executed contracts & retention" },
    department_director: { title: `${user.department} Portfolio`, subtitle: "Contracts in your department" },
    business_unit_rep: { title: "My Requests", subtitle: "Contracts you've submitted and their status" },
    it_admin: { title: "System Overview", subtitle: "All contracts + system health" },
  }[user.role] || { title: "My Work", subtitle: "" };

  const showSubmitter = ["legal_manager", "general_counsel", "it_admin", "compliance"].includes(user.role);

  // Column templates for stat-card drill-downs
  const contractCols = [
    { key: "contract_number", label: "#", render: r => <span className="font-mono text-xs">{r.contract_number}</span> },
    { key: "vendor_legal_name", label: "Vendor", render: r => <span className="font-semibold">{r.vendor_legal_name}</span> },
    { key: "department", label: "Dept" },
    { key: "stage", label: "Stage", render: r => STAGE_LABELS[r.stage] || r.stage },
    { key: "sla", label: "SLA", render: r => <SLABadge status={r.sla_status} remaining={r.sla_remaining} /> },
    { key: "days_total", label: "Days", num: true },
    { key: "amount", label: "$", num: true, render: r => currency(r.contract_amount) },
  ];
  const renewalCols = [
    { key: "contract_number", label: "#", render: r => <span className="font-mono text-xs">{r.contract_number}</span> },
    { key: "vendor", label: "Vendor", render: r => <span className="font-semibold">{r.vendor}</span> },
    { key: "department", label: "Dept" },
    { key: "renewal_date", label: "Renewal", num: true, render: r => new Date(r.renewal_date).toLocaleDateString() },
    { key: "days_until", label: "Days to go", num: true, render: r => (
        <span className={`chip ${r.days_until <= 30 ? "chip-overdue" : r.days_until <= 60 ? "chip-duesoon" : "chip-ontrack"}`}><span className="dot" />{r.days_until}d</span>
      )},
    { key: "amount", label: "Annual $", num: true, render: r => currency(r.amount) },
  ];

  const openStat = (key) => {
    if (key === "assigned") setDrill({ open: true, title: "My Assigned Contracts", rows: assigned, columns: contractCols });
    if (key === "overdue") setDrill({ open: true, title: "My Overdue", rows: overdue, columns: contractCols });
    if (key === "duesoon") setDrill({ open: true, title: "Due Soon (SLA)", rows: dueSoon, columns: contractCols });
    if (key === "renewals") setDrill({ open: true, title: "Renewals ≤90 days", rows: renewals.filter(r => r.days_until <= 90), columns: renewalCols });
  };

  const openKpiDrill = (key) => {
    if (!kpis) return;
    if (key === "cycle" || key === "sla") {
      const rows = all.filter(c => c.stage === "executed" && c.executed_date && c.initial_received_date);
      setDrill({ open: true, title: key === "cycle" ? "Cycle Time — Executed Contracts" : "SLA Compliance — Executed Contracts",
                 rows, columns: contractCols });
    }
    if (key === "throughput") {
      const since = Date.now() - 30 * 86400000;
      const rows = all.filter(c => c.stage === "executed" && c.executed_date && new Date(c.executed_date).getTime() >= since);
      setDrill({ open: true, title: "Executed in Last 30 Days", rows, columns: contractCols });
    }
    if (key === "workload") setDrill({ open: true, title: "All Open Assignments", rows: all.filter(c => c.stage !== "executed"), columns: contractCols });
    if (key === "flags") {
      const rows = all.filter(c => (c.red_flags || []).some(f => !f.resolved && !f.accepted_by_gc));
      setDrill({ open: true, title: "Contracts with Open Red Flags", rows, columns: contractCols });
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-[#005289]">{roleFocus.title}</div>
          <h1 className="text-2xl font-bold text-slate-900">Welcome back, {user.name?.split(" ")[0]}</h1>
          <p className="text-sm text-slate-500 mt-1">{roleFocus.subtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          <ClearedVsPendingWidget summary={summary} onClick={() => nav("/reports")} onMonthClick={(m) => setTrendMonth(m)} />
          <ComplianceSLAChip summary={summary} />
          {["compliance","general_counsel","it_admin"].includes(user.role) && (
            <button onClick={() => setDigestPreview(true)}
                    className="h-9 px-3 text-xs font-semibold rounded border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"
                    data-testid="digest-preview-btn">
              <Eye className="w-3.5 h-3.5" /> Preview Compliance Digest
            </button>
          )}
          <button onClick={() => nav("/intake")} className="px-4 h-9 rounded-md text-white text-sm font-semibold" style={{ background: "#00AE42" }} data-testid="new-intake-btn">
            + New Contract Intake
          </button>
        </div>
      </div>
      <DigestPreviewModal open={digestPreview} onClose={() => setDigestPreview(false)} />
      <ClearedTrendDrill open={!!trendMonth} month={trendMonth} onClose={() => setTrendMonth(null)} />

      <div className="mb-2">
        <ContractEvaluatorLauncher />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="My Assigned" value={assigned.length} icon={FileText} color="#005289" sub={`${active.length} in progress`}
              onClick={() => openStat("assigned")} testid="stat-assigned" />
        <Stat label="My Overdue" value={overdue.length} icon={AlertTriangle} color="#EF4444"
              onClick={overdue.length ? () => openStat("overdue") : null} testid="stat-overdue" />
        <Stat label="Due Soon" value={dueSoon.length} icon={Clock} color="#F59E0B"
              onClick={dueSoon.length ? () => openStat("duesoon") : null} testid="stat-duesoon" />
        <Stat label="Renewals ≤90d" value={renewals.filter(r => r.days_until <= 90).length} icon={RefreshCw} color="#00AE42"
              sub={renewals.length ? `${renewals.length} in next 180d` : "None"}
              onClick={renewals.length ? () => openStat("renewals") : null} testid="stat-renewals" />
      </div>

      {user.role === "compliance" && complianceCounts && (
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="flex items-center gap-2 mb-3"><Shield className="w-4 h-4 text-[#005289]" /><div className="text-xs font-bold uppercase tracking-wider text-slate-500">Compliance Watch</div></div>
          <div className="grid grid-cols-4 gap-4">
            <div><div className="text-2xl font-bold text-red-600">{complianceCounts.open_flags}</div><div className="text-xs text-slate-500">Open Flags</div></div>
            <div><div className="text-2xl font-bold text-[#005289]">{complianceCounts.phi_baa}</div><div className="text-xs text-slate-500">PHI/BAA</div></div>
            <div><div className="text-2xl font-bold text-amber-600">{complianceCounts.checklist_gaps}</div><div className="text-xs text-slate-500">Checklist Gaps</div></div>
            <div><div className="text-2xl font-bold text-red-900">{complianceCounts.high_red}</div><div className="text-xs text-slate-500">High Severity</div></div>
          </div>
        </div>
      )}

      {user.role === "general_counsel" && kpis && (
        <GCKPIPanel kpis={kpis} onDrill={openKpiDrill} onCoach={setCoachUser} />
      )}

      {["legal_manager", "general_counsel", "it_admin"].includes(user.role) && (
        <ReferralsSummaryTile />
      )}

      <UniversityBadge />

      {approverPending.count > 0 && (
        <div className="bg-white border border-amber-200 rounded-md" data-testid="dash-approver-pending">
          <div className="px-5 py-3 border-b border-amber-200 bg-amber-50 flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-amber-700" />
            <h3 className="text-sm font-bold text-amber-900">Waiting on Your Sign-Off</h3>
            <span className="ml-auto text-[11px] text-amber-700 font-semibold">{approverPending.count} contract{approverPending.count === 1 ? "" : "s"}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="hcd-table">
              <thead><tr><th>#</th><th>Vendor</th><th>Department</th><th>Stage</th><th className="num">Amount</th></tr></thead>
              <tbody>
                {approverPending.items.slice(0, 8).map(p => (
                  <tr key={p.id} onClick={() => nav(`/contract/${p.id}`)} className="cursor-pointer" data-testid={`approver-row-${p.contract_number}`}>
                    <td className="font-mono text-xs">{p.contract_number}</td>
                    <td className="font-semibold">{p.vendor}</td>
                    <td className="text-xs text-slate-600">{p.department}</td>
                    <td className="text-xs">{(p.stage || "").replace(/_/g, " ")}</td>
                    <td className="num text-xs">{currency(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {priorityList.length > 0 && (
            <div className="bg-white border border-red-200 rounded-md" data-testid="dash-priority-list">
              <div className="px-5 py-3 border-b border-red-200 bg-red-50 flex items-center gap-2">
                <Flag className="w-4 h-4 text-red-600" />
                <h3 className="text-sm font-bold text-red-900">Priority Contracts</h3>
                <span className="ml-auto text-[11px] text-red-700 font-semibold">{priorityList.length} flagged</span>
              </div>
              <div className="overflow-x-auto">
                <table className="hcd-table">
                  <thead><tr><th>#</th><th>Vendor</th><th>Reason</th><th>Marked By</th><th className="num">Needed By</th><th className="num">Days</th></tr></thead>
                  <tbody>
                    {priorityList.slice(0, 8).map(p => (
                      <tr key={p.contract_id} onClick={() => nav(`/contract/${p.contract_id}`)} className="cursor-pointer" data-testid={`priority-row-${p.contract_number}`}>
                        <td className="font-mono text-xs">{p.contract_number}</td>
                        <td className="font-semibold">{p.vendor}</td>
                        <td className="text-xs text-slate-700 max-w-[220px] truncate" title={p.reason}>{p.reason}</td>
                        <td className="text-xs text-slate-500">{p.marked_by}</td>
                        <td className="num text-xs">{p.needed_by || "—"}</td>
                        <td className="num">
                          {p.days_until != null ? (
                            <span className={`chip ${p.days_until < 0 ? "chip-overdue" : p.days_until <= 7 ? "chip-duesoon" : "chip-ontrack"}`}>
                              <span className="dot" />{p.days_until < 0 ? `${-p.days_until}d over` : `${p.days_until}d`}
                            </span>
                          ) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {overdue.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md" data-testid="dash-overdue-panel">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-600" /><h3 className="text-sm font-bold text-slate-900">My Overdue</h3></div>
              </div>
              <ContractsTable contracts={overdue} onOpen={id => nav(`/contract/${id}`)} team={team} showSubmitter={showSubmitter} />
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-md">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-900">
                {user.role === "department_director" ? "My Department Contracts" :
                 user.role === "business_unit_rep" ? "My Submissions" :
                 user.role === "legal_manager" || user.role === "it_admin" ? "Active Pipeline" :
                 "My Active Queue"}
              </h3>
              <button onClick={() => nav("/pipeline")} className="text-xs font-semibold text-[#005289] hover:underline">View all</button>
            </div>
            <ContractsTable contracts={active.slice(0, 12)} onOpen={id => nav(`/contract/${id}`)} team={team} showSubmitter={showSubmitter} empty="Nothing in your queue right now." />
          </div>
          {executedRecent.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md">
              <div className="px-5 py-3 border-b border-slate-200">
                <h3 className="text-sm font-bold text-slate-900">Recently Executed</h3>
              </div>
              <ContractsTable contracts={executedRecent} onOpen={id => nav(`/contract/${id}`)} team={team} showSubmitter={showSubmitter} />
            </div>
          )}

          {renewals.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md" data-testid="dash-renewal-list">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-[#00AE42]" />
                <h3 className="text-sm font-bold text-slate-900">Auto-Renewal Ledger — days to go</h3>
                <span className="ml-auto text-[11px] text-slate-500">{renewals.length} in next 180 days</span>
              </div>
              <div className="overflow-x-auto">
                <table className="hcd-table">
                  <thead>
                    <tr><th>#</th><th>Vendor</th><th>Dept</th><th className="num">Renewal Date</th><th className="num">Days to Go</th><th className="num">Annual $</th></tr>
                  </thead>
                  <tbody>
                    {renewals.slice(0, 10).map(r => (
                      <tr key={r.contract_id} onClick={() => nav(`/contract/${r.contract_id}`)} className="cursor-pointer" data-testid={`renewal-row-${r.contract_number}`}>
                        <td className="font-mono text-xs">{r.contract_number}</td>
                        <td className="font-semibold">{r.vendor}</td>
                        <td className="text-slate-600">{r.department}</td>
                        <td className="num text-xs">{new Date(r.renewal_date).toLocaleDateString()}</td>
                        <td className="num">
                          <span className={`chip ${r.days_until <= 30 ? "chip-overdue" : r.days_until <= 60 ? "chip-duesoon" : "chip-ontrack"}`}>
                            <span className="dot" />{r.days_until}d
                          </span>
                        </td>
                        <td className="num">{currency(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="space-y-6">
          {upcomingMs.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md" data-testid="upcoming-milestones-card">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#005289]" />
                <h3 className="text-sm font-bold text-slate-900">Upcoming Milestones</h3>
                <span className="ml-auto text-[11px] text-slate-500">{upcomingMs.length}</span>
              </div>
              <div className="p-2 space-y-1 max-h-60 overflow-y-auto">
                {upcomingMs.slice(0, 8).map(m => (
                  <div key={m.milestone_id} onClick={() => nav(`/contract/${m.contract_id}?tab=milestones`)}
                       className="px-3 py-2 rounded hover:bg-slate-50 cursor-pointer text-xs" data-testid={`upcoming-ms-${m.milestone_id}`}>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold border ${m.days_left < 0 ? "bg-red-50 text-red-700 border-red-200" : m.days_left <= 7 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                        {m.days_left < 0 ? `${-m.days_left}d over` : `${m.days_left}d`}
                      </span>
                      <div className="font-semibold text-slate-800 truncate flex-1">{m.label}</div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">#{m.contract_number} · {m.vendor}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {drafts.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md" data-testid="my-drafts-card">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                <FileEdit className="w-4 h-4 text-[#005289]" />
                <h3 className="text-sm font-bold text-slate-900">My Drafts</h3>
                <span className="ml-auto text-[11px] text-slate-500">{drafts.length}</span>
              </div>
              <div className="p-2 space-y-1">
                {drafts.slice(0, 6).map(d => (
                  <div key={d.id} onClick={() => nav(`/intake?draft=${d.id}`)}
                       className="px-3 py-2 rounded hover:bg-slate-50 cursor-pointer text-xs" data-testid={`draft-${d.id}`}>
                    <div className="font-semibold text-slate-800">{d.vendor_legal_name || <span className="italic text-slate-400">(unnamed vendor)</span>}</div>
                    <div className="text-slate-500 mt-0.5">
                      {d.contract_type || "—"} · {d.department || "—"}
                      {d.updated_at && <> · updated {new Date(d.updated_at).toLocaleDateString()}</>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(user.role === "legal_manager" || user.role === "general_counsel" || user.role === "it_admin") && unassignedList.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <h3 className="text-sm font-bold text-slate-900">Unassigned — needs triage</h3>
              </div>
              <div className="p-2 space-y-1">
                {unassignedList.map(c => (
                  <div key={c.id} onClick={() => nav(`/contract/${c.id}`)} className="px-3 py-2 rounded hover:bg-slate-50 cursor-pointer text-xs">
                    <div className="font-mono text-[10px] text-slate-500">{c.contract_number}</div>
                    <div className="font-semibold">{c.vendor_legal_name}</div>
                    <div className="text-slate-500">{c.department} · Submitted by {c.submitter_email || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-md">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2"><Bell className="w-4 h-4 text-[#005289]" /><h3 className="text-sm font-bold text-slate-900">My Alerts</h3></div>
            <div className="max-h-64 overflow-y-auto">
              {unread.length === 0 && <div className="p-4 text-xs text-slate-500 text-center">All caught up.</div>}
              {unread.slice(0, 8).map(n => (
                <div key={n.id} onClick={() => n.contract_id && nav(`/contract/${n.contract_id}`)}
                     className="px-4 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <div className="text-xs font-semibold text-slate-900">{n.title}</div>
                  <div className="text-[11px] text-slate-500">{n.body}</div>
                </div>
              ))}
            </div>
          </div>

          {priorityFeed.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md" data-testid="dash-priority-feed">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                <Activity className="w-4 h-4 text-red-600" />
                <h3 className="text-sm font-bold text-slate-900">Priority Updates</h3>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                {priorityFeed.map((e, i) => (
                  <div key={i} onClick={() => e.contract_id && nav(`/contract/${e.contract_id}`)}
                       className="px-4 py-3 hover:bg-slate-50 cursor-pointer">
                    <div className="flex items-start gap-2">
                      <Flag className={`w-3 h-3 mt-0.5 ${e.action === "priority_set" ? "text-red-600" : "text-slate-400"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs">
                          <span className="font-semibold">{e.actor}</span>{" "}
                          <span className="text-slate-500">{e.action === "priority_set" ? "flagged" : "cleared"}</span>{" "}
                          <span className="font-mono text-[10px] text-[#005289]">{e.contract_number}</span>
                        </div>
                        {e.reason && <div className="text-[11px] text-slate-600 truncate">{e.reason}</div>}
                        <div className="text-[10px] text-slate-400">{new Date(e.at).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {mentions.length > 0 && (
            <div className="text-[11px] text-slate-500">{mentions.length} @-mention{mentions.length===1?"":"s"} pending</div>
          )}
        </div>
      </div>

      <DrillPanel
        open={drill.open}
        title={drill.title}
        rows={drill.rows}
        columns={drill.columns}
        onClose={() => setDrill({ ...drill, open: false })}
        onRow={r => { nav(`/contract/${r.id || r.contract_id}`); setDrill({ ...drill, open: false }); }}
      />
      {coachUser && <CoachModal user={coachUser} onClose={() => setCoachUser(null)} />}
    </div>
  );
}
